import React from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import ResourcesList from './List'

class ResourcesManager {
  constructor() {
    this.containers = new Map()
  }

  init() {
    this.initializeExistingContainers()
    this.observeDOM()
  }

  initializeExistingContainers() {
    const containers = document.querySelectorAll('.resources')
    containers.forEach((container, index) => {
      this.initializeContainer(container, index)
    })
  }

  observeDOM() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList && node.classList.contains('resources')) {
              this.initializeContainer(node, Date.now())
            }
            const containers = node.querySelectorAll ? node.querySelectorAll('.resources') : []
            containers.forEach((container, index) => {
              this.initializeContainer(container, Date.now() + index)
            })
          }
        })
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  }

  initializeContainer(container, id) {
    if (container.dataset.initialized === 'true') {
      return
    }

    let configScript = container.querySelector('script[type="text/javascript"]')

    if (!configScript) {
      configScript = container.parentElement?.querySelector('script[type="text/javascript"]')
    }

    if (!configScript) {
      const allScripts = document.querySelectorAll('script[type="text/javascript"]')
      for (const script of allScripts) {
        if (script.textContent.includes('resourcesConfig')) {
          configScript = script
          break
        }
      }
    }

    if (!configScript) {
      console.warn('No config script found for resources container')
      return
    }

    let config
    try {
      const scriptContent = configScript.textContent

      const configMatch = scriptContent.match(/var\s+resourcesConfig\s*=\s*({.*?});/s)

      if (configMatch) {
        config = JSON.parse(configMatch[1])
      } else {
        console.warn('Could not parse resourcesConfig from script')
        return
      }
    } catch (error) {
      console.error('Error parsing resources config:', error)
      return
    }

    container.dataset.initialized = 'true'

    const root = createRoot(container)

    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: 2,
          refetchOnWindowFocus: false,
        },
      },
    })

    root.render(
      React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(ResourcesList, {
          config: config,
          containerId: id
        })
      )
    )

    this.containers.set(id, { root, container: container, element: container })
  }

  destroy() {
    this.containers.forEach(({ root }) => {
      try {
        root.unmount()
      } catch (error) {
        console.warn('Error unmounting resources component:', error)
      }
    })
    this.containers.clear()
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!window.resourcesManager) {
    window.resourcesManager = new ResourcesManager()
    window.resourcesManager.init()
  }
})

export { ResourcesList, ResourcesManager }
export default ResourcesManager
